import {
  getGroupPerParticipantPriceFormatted,
  getGroupOriginalPerParticipantPriceFormatted,
  getGroupSubtitleProps,
} from './groupPricing'
import {
  getTieredFromPricePerPerson,
  getTieredSubtitleProps,
} from './tieredPricing'
import {
  getStandardFormattedSubtitle,
  getStandardSubtitleProps,
} from './standardPricing'
import type { PricingVariant, SubtitleProps, BadgeProps } from './types'
import type { EventDescription, Pricings } from '~/composables/types/event'
import { useEventPricingsGetters } from '~/composables/useEventPricingsGetters'
import { formatPriceAmount } from '~/utils/currencyHelpers'
import { convertLocaleIntoBCP47 } from '~/utils/localeHelpers'
import { eventGetters } from '~/utils/eventGetters'

export type { PricingVariant, SubtitleProps, BadgeProps } from './types'

export const useEventBuyBoxPrice = (
  pricings: Ref<Record<string, Pricings>>,
  context: {
    dateId: Ref<string | undefined>
    quantity: Ref<number>
    event: Ref<EventDescription | undefined>
    isGiftcard: Ref<boolean>
    type: Ref<'PUBLIC' | 'PRIVATE' | 'PUBLIC_PRIVATE'>
    loadingPricings: Ref<boolean>
  }
) => {
  const { t, locale } = useI18n()

  const { totalPrice, hasSale, pricingData } = useEventPricingsGetters(
    pricings,
    {
      dateId: context.dateId,
      quantity: context.quantity,
      event: context.event,
    }
  )

  const isGroupPricing = computed(() =>
    eventGetters.isGroupPricing(context.event.value)
  )

  const isKitsOrGiftcardEvent = computed(
    () =>
      eventGetters.isBoxEvent(context.event.value) ||
      eventGetters.isGiftcardEvent(context.event.value) ||
      context.isGiftcard.value
  )

  const hasSingleCategory = computed(() => {
    const categories =
      context.event.value?.pricing?.data?.pricingCategories?.data
    return Boolean(categories && categories.length === 1)
  })

  const hasTiers = computed(() =>
    eventGetters.hasTieredPricing(context.event.value)
  )

  // Shared: original price per person from pricing breakdown
  const originalPricePerPerson = computed(() => {
    const originalPrice =
      pricingData.value?.price_breakdown?.[0]?.original_price_per_person
    const currentPrice =
      pricingData.value?.price_breakdown?.[0]?.price_per_person?.formatted
    if (!originalPrice || originalPrice.formatted === currentPrice) {
      return undefined
    }
    return originalPrice.formatted
  })

  // Group pricing: per participant = total price / quantity
  const groupPerParticipantFormatted = computed(() =>
    isGroupPricing.value
      ? getGroupPerParticipantPriceFormatted(
          pricingData.value,
          context.quantity.value ?? 1,
          convertLocaleIntoBCP47(locale.value)
        )
      : undefined
  )

  // Group pricing: original (pre-promotion) per participant = base_price / quantity
  const groupOriginalPerParticipantFormatted = computed(() =>
    isGroupPricing.value
      ? getGroupOriginalPerParticipantPriceFormatted(
          pricingData.value,
          context.quantity.value ?? 1,
          convertLocaleIntoBCP47(locale.value)
        )
      : undefined
  )

  // Use event.has_promotions when loading (no pricing data) to show correct skeleton
  const hasSaleOrPromotion = computed(
    () =>
      hasSale.value ||
      (!!context.event.value?.has_promotions && !pricingData.value) ||
      false
  )

  const groupSubtitleProps = computed(
    (): SubtitleProps =>
      isGroupPricing.value
        ? getGroupSubtitleProps(
            groupPerParticipantFormatted.value,
            groupOriginalPerParticipantFormatted.value,
            hasSaleOrPromotion.value,
            t
          )
        : null
  )

  // Tiered pricing
  const fromPricePerPerson = computed(() =>
    hasTiers.value
      ? getTieredFromPricePerPerson(
          context.event.value,
          convertLocaleIntoBCP47(locale.value),
          t
        )
      : undefined
  )

  const tieredSubtitleProps = computed(
    (): SubtitleProps =>
      hasTiers.value && fromPricePerPerson.value
        ? getTieredSubtitleProps(fromPricePerPerson.value)
        : null
  )

  // Standard pricing
  const standardFormattedSubtitle = computed(() =>
    getStandardFormattedSubtitle(
      context.event.value,
      originalPricePerPerson.value,
      hasSale.value,
      hasSingleCategory.value,
      t
    )
  )

  const standardSubtitleProps = computed(
    (): SubtitleProps =>
      standardFormattedSubtitle.value
        ? getStandardSubtitleProps(standardFormattedSubtitle.value)
        : null
  )

  // Variant determination (order matters: group > tiered > standard)
  const variant = computed<PricingVariant>(() => {
    if (context.isGiftcard.value) return 'giftcard'
    if (eventGetters.isBoxEvent(context.event.value)) return 'kit'
    if (isGroupPricing.value) return 'group'
    if (hasTiers.value) return 'tiered'
    return 'standard'
  })

  // Subtitle for current variant
  const subtitleProps = computed((): SubtitleProps => {
    if (variant.value === 'giftcard') return null
    if (variant.value === 'group') return groupSubtitleProps.value
    if (variant.value === 'tiered') return tieredSubtitleProps.value
    if (variant.value === 'standard' || variant.value === 'kit') {
      return standardSubtitleProps.value
    }
    return null
  })

  // Main price to display (total price for all variants including group)
  const mainPriceToDisplay = computed(() => {
    if (totalPrice.value) return totalPrice.value
    if (isKitsOrGiftcardEvent.value && !context.loadingPricings.value) {
      const priceAmount = context.isGiftcard.value
        ? eventGetters.getRetailPriceAmount(context.event.value)
        : eventGetters.getDefaultPriceAmount(context.event.value)
      if (priceAmount) {
        const qty = context.quantity.value ?? 1
        const totalInCents = Number(priceAmount) * qty
        const currency =
          eventGetters.getDefaultPriceCurrency(context.event.value) || 'EUR'
        return formatPriceAmount(
          totalInCents,
          convertLocaleIntoBCP47(locale.value),
          currency,
          totalInCents % 100 === 0 ? 0 : 2
        )
      }
    }
    return undefined
  })

  // Badge props
  const badgeProps = computed((): BadgeProps => {
    if (context.isGiftcard.value) {
      return { variant: 'none', tooltipText: '' }
    }
    const quantity = context.quantity.value ?? 1
    const groupPricingMaxParticipants =
      eventGetters.getGroupPricingMaxParticipants(context.event.value)
    const maxPeopleForQuantity =
      eventGetters.getGroupPricingMaxParticipantsForQuantity(
        context.event.value,
        quantity
      )

    // Show "per group up to X" where X is the max_participants of the matching scale
    // Hide when quantity exceeds all groups (hybrid - no matching scale)
    const showPerGroupUpTo =
      isGroupPricing.value &&
      maxPeopleForQuantity != null &&
      (groupPricingMaxParticipants == null ||
        quantity <= groupPricingMaxParticipants)

    if (showPerGroupUpTo) {
      return {
        variant: 'perGroupUpTo',
        maxPeople: maxPeopleForQuantity,
        tooltipText: t('product.show.checkout.info.privateEvent', {
          price: totalPrice.value,
        }),
      }
    }
    if (context.type.value === 'PRIVATE') {
      return {
        variant: 'private',
        tooltipText: t('product.show.checkout.info.privateEvent', {
          price: totalPrice.value,
        }),
      }
    }
    return {
      variant: 'info',
      tooltipText: t('product.show.checkout.info.publicEvent', {
        price: totalPrice.value,
      }),
    }
  })

  const tooltipText = computed(() =>
    t(
      context.isGiftcard.value
        ? 'product.show.checkout.info.giftcard'
        : context.type.value === 'PUBLIC'
          ? 'product.show.checkout.info.publicEvent'
          : 'product.show.checkout.info.privateEvent',
      { price: totalPrice.value }
    )
  )

  return {
    variant,
    subtitleProps,
    badgeProps,
    mainPriceToDisplay,
    tooltipText,
    hasSale,
    pricingData,
  }
}
